import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import CertificateModal from './CertificateModal';
import { toast } from 'react-toastify';
import DeleteModel from '../Common/DeleteModel';
import { useAuth } from '../../Context/AuthContext';
import { saveProviderCertificates, deleteProviderCertificate, getAllProviderBoardCertificates, getAllProviderAdditionalCertificates, getAllProviderProfessionalCertificates } from '../../Services/ProviderService';

const emptyBoardCertification = {
  id: null,
  provider_id: null,
  status_id: null,
  file_id: null,
  board_name: '',
  is_primary_board: '',
  is_board_eligible: '',
  certifications: '',
  cert_status: '',
  cert_type: '',
  exam_passed: '',
  speciality_exam_passed: '',
  no_of_speciality_exam_attempts: '',
  exam_date: '',
  certificate_duration: '',
  issue_date: '',
  expiration_date: '',
  recertification_date: '',
  moc_occ_status: '',
  meeting_moc_occ: '',
  type: 'create',
  document: null,
};

const emptyProfessionalOrganization = {
  ...emptyBoardCertification,
  board_name: '',
};

const emptyAdditionalCertificate = {
  ...emptyBoardCertification,
  issuer: '',
  board_name: '',
  is_primary_board: '',
  is_board_eligible: '',
};

export default function Certifications({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();

  const [boardCertifications, setBoardCertifications] = useState(
    Array.isArray(initialData?.boardCertifications) ? initialData.boardCertifications : []
  );
  const [professionalOrganizations, setProfessionalOrganizations] = useState(
    Array.isArray(initialData?.professionalOrganizations)
      ? initialData.professionalOrganizations
      : []
  );
  const [additionalCertificates, setAdditionalCertificates] = useState(
    Array.isArray(initialData?.additionalCertificates) ? initialData.additionalCertificates : []
  );

  const [boardLoading, setBoardLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [additionalLoading, setAdditionalLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [showBoardModal, setShowBoardModal] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  const [editingBoardIndex, setEditingBoardIndex] = useState(null);
  const [editingOrganizationIndex, setEditingOrganizationIndex] = useState(null);
  const [editingCertificateIndex, setEditingCertificateIndex] = useState(null);

  const [boardForm, setBoardForm] = useState(emptyBoardCertification);
  const [organizationForm, setOrganizationForm] = useState(emptyProfessionalOrganization);
  const [certificateForm, setCertificateForm] = useState(emptyAdditionalCertificate);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, section: null, index: null, item: null, isLoading: false });

  const resetBoardForm = () => setBoardForm(emptyBoardCertification);
  const resetOrganizationForm = () => setOrganizationForm(emptyProfessionalOrganization);
  const resetCertificateForm = () => setCertificateForm(emptyAdditionalCertificate);

  const handleBoardAdd = () => {
    resetBoardForm();
    setEditingBoardIndex(null);
    setShowBoardModal(true);
  };

  const handleOrganizationAdd = () => {
    resetOrganizationForm();
    setEditingOrganizationIndex(null);
    setShowOrganizationModal(true);
  };

  const handleCertificateAdd = () => {
    resetCertificateForm();
    setEditingCertificateIndex(null);
    setShowCertificateModal(true);
  };

  const handleBoardEdit = (index) => {
    setBoardForm(boardCertifications[index]);
    setEditingBoardIndex(index);
    setShowBoardModal(true);
  };

  const handleOrganizationEdit = (index) => {
    setOrganizationForm(professionalOrganizations[index]);
    setEditingOrganizationIndex(index);
    setShowOrganizationModal(true);
  };

  const handleCertificateEdit = (index) => {
    setCertificateForm(additionalCertificates[index]);
    setEditingCertificateIndex(index);
    setShowCertificateModal(true);
  };

  const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
  const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

  const getProviderIdFromStorage = () => {
    try {
      return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read provider ID from localStorage:', error);
      return null;
    }
  };

  const getOrganizationIdFromStorage = () => {
    try {
      return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read organization ID from localStorage:', error);
      return null;
    }
  };

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const refreshSection = async (section) => {
    const currentProviderId = providerId || getProviderIdFromStorage();
    if (!token || !currentProviderId) return [];

    try {
      if (section === 'board') setBoardLoading(true);
      if (section === 'organization') setOrgLoading(true);
      if (section === 'additional') setAdditionalLoading(true);

      switch (section) {
        case 'board': {
          const response = await getAllProviderBoardCertificates(token, currentProviderId);
          if (response?.is_success) {
            setBoardCertifications(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return [];
        }
        case 'organization': {
          const response = await getAllProviderProfessionalCertificates(token, currentProviderId);
          if (response?.is_success) {
            setProfessionalOrganizations(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return [];
        }
        case 'additional': {
          const response = await getAllProviderAdditionalCertificates(token, currentProviderId);
          if (response?.is_success) {
            setAdditionalCertificates(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return [];
        }
        default:
          return [];
      }
    } catch (error) {
      console.error(`Failed to refresh ${section} certificates:`, error);
      toast.error('Unable to refresh certificate list.');
      return [];
    } finally {
      if (section === 'board') setBoardLoading(false);
      if (section === 'organization') setOrgLoading(false);
      if (section === 'additional') setAdditionalLoading(false);
    }
  };

  const saveBoardCertification = async (values) => {
    const ok = await performSave('board', values);
    if (ok) {
      setShowBoardModal(false);
      setEditingBoardIndex(null);
      resetBoardForm();
    }
  };

  const saveOrganization = async (values) => {
    const ok = await performSave('organization', values);
    if (ok) {
      setShowOrganizationModal(false);
      setEditingOrganizationIndex(null);
      resetOrganizationForm();
    }
  };

  const saveCertificate = async (values) => {
    const ok = await performSave('additional', values);
    if (ok) {
      setShowCertificateModal(false);
      setEditingCertificateIndex(null);
      resetCertificateForm();
    }
  };

  const deleteBoardCertification = async (index) => {
    await performDelete('board', index);
  };

  const deleteOrganization = async (index) => {
    await performDelete('organization', index);
  };

  const deleteCertificate = async (index) => {
    await performDelete('additional', index);
  };

  const openDeleteModal = (section, index) => {
    const items = section === 'board'
      ? [...boardCertifications]
      : section === 'organization'
        ? [...professionalOrganizations]
        : [...additionalCertificates];
    setDeleteModal({ isOpen: true, section, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, section: null, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.section === null || deleteModal.index === null || deleteModal.index === undefined) return;

    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      if (deleteModal.section === 'board') {
        await deleteBoardCertification(deleteModal.index);
      } else if (deleteModal.section === 'organization') {
        await deleteOrganization(deleteModal.index);
      } else {
        await deleteCertificate(deleteModal.index);
      }
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  useEffect(() => {
    if (!token) return;

    const loadAll = async () => {
      setBoardLoading(true);
      setOrgLoading(true);
      setAdditionalLoading(true);
      try {
        await Promise.all([refreshSection('board'), refreshSection('organization'), refreshSection('additional')]);
      } finally {
        setBoardLoading(false);
        setOrgLoading(false);
        setAdditionalLoading(false);
      }
    };

    loadAll();
  }, [token, providerId]);

  const performSave = async (section, values) => {
    if (!token) return;
    setModalLoading(true);
    try {
      const currentProviderId = providerId || getProviderIdFromStorage();
      const organizationId = getOrganizationIdFromStorage();
      const certTypeMap = {
        board: 47,
        organization: 48,
        additional: 49,
      };
      const payload = {
        ...values,
        provider_id: currentProviderId,
        organization_id: organizationId,
        cert_type: certTypeMap[section],
        type: values.type ?? (values.id ? 'update' : 'create'),
      };
      const response = await saveProviderCertificates(token, payload);
      if (response?.is_success) {
        const updated = await refreshSection(section);
        if (onSaveSuccess) onSaveSuccess(updated);
        toast.success(response.message || 'Certificate saved successfully');
        return true;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save certificate');
      }
      return false;
    } catch (error) {
      console.error('Error saving provider certificate:', error);
      toast.error('Error saving provider certificate');
      return false;
    } finally {
      setModalLoading(false);
    }
  };

  const performDelete = async (section, index) => {
    const items = section === 'board' ? [...boardCertifications] : section === 'organization' ? [...professionalOrganizations] : [...additionalCertificates];
    const itemToDelete = items[index];
    const certId = itemToDelete?.id ?? itemToDelete?.certificate_id;

    const optimisticUpdate = (updated) => {
      if (section === 'board') setBoardCertifications(updated);
      if (section === 'organization') setProfessionalOrganizations(updated);
      if (section === 'additional') setAdditionalCertificates(updated);
      if (onSaveSuccess) onSaveSuccess(updated);
    };

    if (!certId) {
      const updated = items.filter((_, i) => i !== index);
      optimisticUpdate(updated);
      return;
    }

    setModalLoading(true);
    try {
      const response = await deleteProviderCertificate(token, certId);
      if (response?.is_success) {
        const refreshed = await refreshSection(section);
        if (Array.isArray(refreshed)) {
          optimisticUpdate(refreshed);
        } else {
          const updated = items.filter((_, i) => i !== index);
          optimisticUpdate(updated);
        }
        toast.success(response.message || 'Certificate deleted successfully');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to delete certificate');
      }
    } catch (error) {
      console.error('Error deleting provider certificate:', error);
      toast.error('Error deleting provider certificate');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <div className="container-fluid p-0">
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold hc-text-primary m-0">Board Certifications</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
              onClick={handleBoardAdd}
            >
              <MdAdd size={18} />
              Add
            </button>
          </div>

          <CertificateModal
            isOpen={showBoardModal}
            onClose={() => {
              setShowBoardModal(false);
              setEditingBoardIndex(null);
              resetBoardForm();
            }}
            onSave={saveBoardCertification}
            initialData={editingBoardIndex !== null ? boardForm : null}
            sectionType="board"
            providerId={providerId}
            isLoading={modalLoading}
          />

          
          {boardLoading ?(
            <div className="text-center py-4 text-muted small">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : boardCertifications.length === 0 ? (
            <div className="text-center py-4 text-muted small">
             No board certifications added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              boardCertifications.map((item, index) => (
                <React.Fragment key={item.id ?? index}>

              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Certification
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.certifications || '—'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Issuing Board
                    </div>

                    <div className="fw-semibold text-dark text-truncate d-block"
                     data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    data-bs-container="body"
                    title={item.board_name_name || '—'}
                    >
                     {item.board_name_name || '—'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Issued
                    </div>

                    <div className="fw-semibold text-dark">
                    {item.issue_date || '—'}
                    </div>

                  </div>
                </div>
              </div>




              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => handleBoardEdit(index)}
                          >
                            <MdEdit size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger rounded-3"
                            onClick={() => openDeleteModal('board', index)}
                          >
                            <MdDelete size={16} />
                          </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}

        </div>

        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold hc-text-primary m-0">Professional Organizations</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
              onClick={handleOrganizationAdd}
            >
              <MdAdd size={18} />
              Add
            </button>
          </div>

      
          <CertificateModal
            isOpen={showOrganizationModal}
            onClose={() => {
              setShowOrganizationModal(false);
              setEditingOrganizationIndex(null);
              resetOrganizationForm();
            }}
            onSave={saveOrganization}
            initialData={editingOrganizationIndex !== null ? organizationForm : null}
            sectionType="organization"
            providerId={providerId}
            isLoading={modalLoading}
          />



          {orgLoading ?(
            <div className="text-center py-4 text-muted small">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : professionalOrganizations.length === 0 ? (
            <div className="text-center py-4 text-muted small">
             No board certifications added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              professionalOrganizations.map((item, index) => (
                <React.Fragment key={item.id ?? index}>

              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Organization
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.name || '—'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Role
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.role || '—'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Since
                    </div>

                    <div className="fw-semibold text-dark">
                    {item.startDate || '—'}
                    </div>

                  </div>
                </div>
              </div>




              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                     <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => handleOrganizationEdit(index)}>
                            <MdEdit size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger rounded-3"
                            onClick={() => openDeleteModal('organization', index)}>
                            <MdDelete size={16} />
                          </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}

        </div>





        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold hc-text-primary m-0">Additional Certificates</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
              onClick={handleCertificateAdd}
            >
              <MdAdd size={18} />
              Add
            </button>
          </div>

          <CertificateModal
            isOpen={showCertificateModal}
            onClose={() => {
              setShowCertificateModal(false);
              setEditingCertificateIndex(null);
              resetCertificateForm();
            }}
            onSave={saveCertificate}
            initialData={editingCertificateIndex !== null ? certificateForm : null}
            sectionType="additional"
            providerId={providerId}
            isLoading={modalLoading}
          />

          {additionalLoading ?(
            <div className="text-center py-4 text-muted small">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : additionalCertificates.length === 0 ? (
            <div className="text-center py-4 text-muted small">
             No additional certificates added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              additionalCertificates.map((item, index) => (
                <React.Fragment key={item.id ?? index}>

              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Certificate
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.name || '—'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Issuer
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.issuer || '—'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Issued
                    </div>

                    <div className="fw-semibold text-dark">
                    {item.issuedDate || '—'}
                    </div>

                  </div>
                </div>
              </div>




              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                     <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-3"
                      onClick={() => handleCertificateEdit(index)}
                    >
                      <MdEdit size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger rounded-3"
                      onClick={() => openDeleteModal('additional', index)}
                    >
                      <MdDelete size={16} />
                    </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}

        </div>
      </div>

      

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="certificate"
        itemName={deleteModal.item ? (deleteModal.item.certifications || deleteModal.item.name || deleteModal.item.board_name || deleteModal.item.issuer || 'this certificate') : 'this certificate'}
        description={deleteModal.section === 'organization'
          ? 'This will permanently delete this professional organization entry. This action cannot be undone.'
          : deleteModal.section === 'additional'
            ? 'This will permanently delete this additional certificate. This action cannot be undone.'
            : 'This will permanently delete this board certification. This action cannot be undone.'}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}
